import React, { useEffect, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { shopApi, formatApiError } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Mail, KeyRound, Save } from "lucide-react";

/**
 * Owner/Manager clickable name pill that opens a popover with:
 *   • email (view + update)
 *   • change my PIN (current + new)
 * Staff should never see this — the parent gates by role.
 */
export function MyProfilePopover({ slug, role, personName }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [emailBusy, setEmailBusy] = useState(false);
  const [curPin, setCurPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [pinBusy, setPinBusy] = useState(false);
  const [loadedEmail, setLoadedEmail] = useState(null); // null = not loaded yet

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await shopApi(slug).get(`/shops/${slug}/me`);
        if (cancelled) return;
        setEmail(r.data.email || "");
        setLoadedEmail(r.data.email || "");
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || t("profile.load_failed"));
      }
    })();
    return () => { cancelled = true; };
  }, [open, slug, t]);

  const saveEmail = async () => {
    setEmailBusy(true);
    try {
      const r = await shopApi(slug).post(`/shops/${slug}/change-my-email`, { email: email.trim() || null });
      setLoadedEmail(r.data.email || "");
      toast.success(t("profile.email_updated"));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setEmailBusy(false); }
  };

  const changePin = async () => {
    setPinBusy(true);
    try {
      await shopApi(slug).post(`/shops/${slug}/change-my-pin`, { current_pin: curPin, new_pin: newPin });
      toast.success(t("hub.pin_changed"));
      setCurPin(""); setNewPin("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally { setPinBusy(false); }
  };

  const emailChanged = loadedEmail !== null && (email.trim() || null) !== (loadedEmail || null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="hidden sm:inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-100 hover:bg-emerald-100 hover:border-emerald-200 px-2.5 py-1 text-xs font-mono text-emerald-900 transition"
          data-testid="shop-session-person"
          aria-label={t("profile.open")}
        >
          <span className="uppercase tracking-wider text-[10px] text-emerald-700">{role}</span>
          <span className="text-stone-400">·</span>
          <span>{personName}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80" data-testid="my-profile-popover">
        <div className="mb-3">
          <div className="caption-label text-emerald-700">{role}</div>
          <div className="font-serif text-lg text-stone-900 leading-tight">{personName}</div>
        </div>

        <div className="space-y-2 pb-3 border-b border-stone-200">
          <Label className="caption-label flex items-center gap-1"><Mail className="w-3 h-3" /> {t("common.email")}</Label>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="name@example.com"
            data-testid="profile-email-input"
            autoComplete="off"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={saveEmail}
              disabled={emailBusy || !emailChanged}
              className="bg-emerald-800 hover:bg-emerald-900"
              data-testid="profile-email-save"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {t("profile.save_email")}
            </Button>
          </div>
        </div>

        <div className="space-y-2 pt-3">
          <Label className="caption-label flex items-center gap-1"><KeyRound className="w-3 h-3" /> {t("hub.change_my_pin")}</Label>
          <Input
            inputMode="numeric"
            maxLength={4}
            value={curPin}
            onChange={(e) => setCurPin(e.target.value.replace(/\D/g, ""))}
            placeholder={t("common.current_pin")}
            data-testid="profile-pin-current"
          />
          <Input
            inputMode="numeric"
            maxLength={4}
            value={newPin}
            onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
            placeholder={t("common.new_pin")}
            data-testid="profile-pin-new"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={changePin}
              disabled={pinBusy || curPin.length !== 4 || newPin.length !== 4}
              className="bg-emerald-800 hover:bg-emerald-900"
              data-testid="profile-pin-save"
            >
              <Save className="w-3.5 h-3.5 mr-1" />
              {t("common.save")}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
